import i18n from 'i18next';
import {
  ArrowRightLeft, Ban, Banknote, BookOpen, Building2, CalendarClock, CalendarRange, CircleDollarSign, CreditCard, Eye, FileText, GraduationCap, Landmark, Link2, MapPin,
  MessageSquare, Pause, Pencil, Play, Plus, Receipt, Send, Settings2, ShieldCheck, Tag as TagIcon, Trash2, Upload, UserCheck, UserMinus, UserPlus, Users, UserX,
  CalendarDays, LayoutGrid, type LucideIcon,
} from 'lucide-react';
import { auditGroupOf, type AuditGroup } from '@repo/shared-types';
import { formatVnd } from './formatVnd';

/**
 * One home for how an AuditLog action reads on screen: its label key, its
 * icon, its one-line summary, its timestamp. Cài đặt › Nhật ký thao tác and
 * the student page's "Hoạt động" both render from here, so a label added for
 * one is never missing on the other (2026-09-08: the student page had its
 * own shorter map and shipped STUDENT_PROGRESS_LINK_CREATED as a raw enum
 * after the org page had already learned it).
 *
 * Labels live in the settings namespace under `audit.actions.*`; callers
 * translate with a `t` that has that namespace loaded (`useTranslation('settings')`
 * or an array including it).
 */
export const AUDIT_LABEL_NS = 'settings';

export function auditActionLabelKey(action: string): string {
  return `audit.actions.${action}`;
}

export function hasAuditActionLabel(action: string): boolean {
  return i18n.exists(`${AUDIT_LABEL_NS}:${auditActionLabelKey(action)}`);
}

/** `AuditLog.createdAt` is a true instant, so the absolute stamp is composed
 *  in Vietnam time from named parts (INVARIANTS §2.3: never the viewer's zone,
 *  and never trust a locale's separator — see lib/matchedAt.ts). */
const VN_STAMP = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Ho_Chi_Minh',
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
});
// Session `startAt` values are the house frame (VN wall-clock stored as
// UTC-naive, INVARIANTS §2.1): read the ISO parts as written, never through a
// timezone, or an 18:00 class prints as 01:00 the next day.
export function formatHouseStamp(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
}
const houseStamp = (v: unknown): string => (typeof v === 'string' ? formatHouseStamp(v) : '');

export function formatVnStamp(at: Date): string {
  const p = Object.fromEntries(VN_STAMP.formatToParts(at).map((x) => [x.type, x.value]));
  // `hour12: false` in some ICU builds still yields "24" at midnight.
  const hour = p.hour === '24' ? '00' : p.hour;
  return `${p.day}/${p.month}/${p.year} ${hour}:${p.minute}`;
}

export const AUDIT_GROUP_ICONS: Record<AuditGroup, LucideIcon> = {
  money: CircleDollarSign,
  students: GraduationCap,
  clients: Users,
  org: Building2,
  platform: ShieldCheck,
  other: Pencil,
};

/** One icon per action where the shape says more than the group (design 13
 *  §5, all muted); anything else falls back to its group's icon. */
const ACTION_ICONS: Record<string, LucideIcon> = {
  PAYER_REASSIGNED: ArrowRightLeft,
  STUDENT_TAGS_CHANGED: TagIcon,
  STUDENT_DEACTIVATED: UserX,
  STUDENT_REACTIVATED: UserCheck,
  STUDENT_DELETED: Trash2,
  STUDENT_PROGRESS_LINK_CREATED: Link2,
  STUDENT_PROGRESS_LINK_ROTATED: Link2,
  STUDENT_PROGRESS_LINK_BULK_CREATED: Link2,
  ENROLLMENT_CREATED: Plus,
  ENROLLMENT_PAUSED: Pause,
  ENROLLMENT_RESUMED: Play,
  ENROLLMENT_ENDED: Ban,
  ENROLLMENT_RATE_CHANGED: CircleDollarSign,
  ENROLLMENT_DELETED: Trash2,
  ENROLLMENT_CHARGE_ABSENT_CHANGED: Settings2,
  ENROLLMENT_CHARGE_ABSENT_BULK: Settings2,
  SLOT_MEMBER_MOVED: ArrowRightLeft,
  INVOICE_CLIENT_CHANGED: FileText,
  INVOICE_DELETED: Trash2,
  MONTH_CLOSED: FileText,
  CLIENT_INVOICE_SENT: Send,
  CLIENT_REMINDER_SENT: Send,
  CLIENT_INVOICE_VIEWED: Eye,
  CLIENT_INVOICE_PAID: CircleDollarSign,
  CLIENT_NOTE_ADDED: MessageSquare,
  CLIENT_NOTE_EDITED: MessageSquare,
  CLIENT_TAG_ADDED: TagIcon,
  CLIENT_TAG_REMOVED: TagIcon,
  ORG_SETTINGS_UPDATED: Settings2,
  ORG_PREFERENCES_UPDATED: Settings2,
  MEMBER_INVITED: UserPlus,
  MEMBER_PLACEHOLDER_CREATED: UserPlus,
  MEMBER_CONVERTED: UserCheck,
  MEMBER_REMOVED: UserMinus,
  BANK_LINKED: Landmark,
  BANK_UNLINKED: Landmark,
  BANK_PRIMARY_CHANGED: Landmark,
  BRANCH_CREATED: MapPin,
  BRANCH_UPDATED: MapPin,
  BRANCH_ARCHIVED: MapPin,
  BRANCH_UNARCHIVED: MapPin,
  BRANCH_SLOTS_ASSIGNED: MapPin,
  SUBSCRIPTION_UPGRADE_REQUESTED: CreditCard,
  SUBSCRIPTION_RENEWAL_STOPPED: CreditCard,
  SUBSCRIPTION_RENEWAL_RESUMED: CreditCard,
  SUBSCRIPTION_INVOICE_CANCELLED: CreditCard,
  INVOICE_UPDATED: FileText,
  INVOICE_DUE_DATE_BULK: CalendarClock,
  PAYMENT_RECORDED: Banknote,
  PAYMENT_LINKED: Link2,
  PAYMENT_IGNORED: Ban,
  PAYMENT_DELETED: Trash2,
  EXPENSE_CREATED: Receipt,
  EXPENSE_UPDATED: Receipt,
  EXPENSE_DELETED: Trash2,
  PAYROLL_ADJUSTMENT_CREATED: Banknote,
  PAYROLL_ADJUSTMENT_DELETED: Trash2,
  STUDENT_CREATED: Plus,
  STUDENT_UPDATED: Pencil,
  STUDENTS_IMPORTED: Upload,
  CLIENT_CREATED: UserPlus,
  CLIENT_DELETED: Trash2,
  CLIENT_NOTE_DELETED: Trash2,
  SUBJECT_CREATED: BookOpen,
  SUBJECT_UPDATED: BookOpen,
  SUBJECT_DELETED: Trash2,
  SCHEDULE_TEMPLATE_CREATED: CalendarRange,
  SCHEDULE_TEMPLATE_UPDATED: CalendarRange,
  SCHEDULE_TEMPLATE_DELETED: Trash2,
  SCHEDULE_TEMPLATE_ENTRIES_REPLACED: CalendarRange,
  SLOT_CREATED: LayoutGrid,
  SLOT_UPDATED: LayoutGrid,
  SLOT_ARCHIVED: LayoutGrid,
  SLOT_UNARCHIVED: LayoutGrid,
  SLOT_MEMBER_ADDED: UserPlus,
  SLOT_MEMBER_REMOVED: UserMinus,
  CLASS_CREATED: LayoutGrid,
  CLASS_UPDATED: Pencil,
  CLASS_ARCHIVED: LayoutGrid,
  CLASS_UNARCHIVED: LayoutGrid,
  CLASS_MEMBERS_ADDED: UserPlus,
  SESSION_CREATED: CalendarDays,
  SESSION_UPDATED: CalendarDays,
  SESSION_CANCELLED: Ban,
  SESSION_PARTICIPANT_ADDED: UserPlus,
  SESSION_PARTICIPANT_REMOVED: UserMinus,
  SESSION_PARTICIPANT_MOVED: ArrowRightLeft,
};

export function auditActionIcon(action: string, group: AuditGroup = auditGroupOf(action)): LucideIcon {
  return ACTION_ICONS[action] ?? AUDIT_GROUP_ICONS[group] ?? Pencil;
}

type Meta = Record<string, unknown>;
const asMeta = (m: unknown): Meta => (m && typeof m === 'object' && !Array.isArray(m) ? (m as Meta) : {});
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** One short Vietnamese line per action, from the metadata the writer stored.
 *  `t` must resolve `audit.summary.*` in the settings namespace.
 *  Returns null when the label alone says it all. Unknown actions fall back to
 *  a few primitive `key: value` pairs, ids and timestamps left out. */
export function summarizeAudit(row: { action: string; metadata: unknown }, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  const m = asMeta(row.metadata);
  switch (row.action) {
    case 'INVOICE_DELETED': {
      const parts = [str(m.invoiceCode), str(m.clientName), num(m.totalAmount) !== null ? formatVnd(num(m.totalAmount)) : null].filter(Boolean);
      const bulk = m.bulk === true ? ` (${t('audit.summary.bulk')})` : '';
      return parts.length ? parts.join(' · ') + bulk : null;
    }
    case 'MONTH_CLOSED':
      return t('audit.summary.monthClosed', { month: m.month, year: m.year, count: num(m.created) ?? 0, total: formatVnd(num(m.totalVnd) ?? 0) });
    case 'CLIENT_INVOICE_CREATED':
    case 'CLIENT_INVOICE_PAID':
    case 'CLIENT_INVOICE_SENT':
    case 'CLIENT_INVOICE_VIEWED':
    case 'CLIENT_REMINDER_SENT':
    case 'CLIENT_INVOICE_OVERDUE':
    case 'CLIENT_INVOICE_CANCELLED': {
      const channel = str(m.channel);
      const parts = [
        str(m.invoiceCode),
        num(m.amount) !== null ? formatVnd(num(m.amount)) : null,
        channel === 'email' ? t('audit.summary.channelEmail') : channel ? t('audit.summary.channelZalo') : null,
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'CLIENT_TAG_ADDED':
    case 'CLIENT_TAG_REMOVED':
      return str(m.tag) ? t('audit.summary.tag', { tag: str(m.tag) }) : null;
    case 'CLIENT_NOTE_ADDED':
    case 'CLIENT_NOTE_EDITED':
      return str(m.contentPreview);
    case 'CLIENT_INFO_UPDATED':
      return str(m.field) ? t('audit.summary.fromTo', { from: String(m.oldValue ?? ''), to: String(m.newValue ?? '') }) : null;
    case 'ORG_INFO_UPDATED':
    case 'ORG_SETTINGS_UPDATED':
    case 'ORG_PREFERENCES_UPDATED':
    case 'BRANCH_UPDATED': {
      const field = str(m.field);
      if (!field) return null;
      const label = i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${field}`) ? t(`audit.fields.${field}`) : field;
      const show = (v: unknown) => (v === null || v === undefined || v === '' ? t('audit.summary.empty') : typeof v === 'boolean' ? (v ? t('audit.summary.on') : t('audit.summary.off')) : String(v).slice(0, 60));
      return t('audit.summary.fieldFromTo', { field: label, from: show(m.from), to: show(m.to) });
    }
    case 'MEMBER_INVITED':
      return str(m.email) ? t('audit.summary.memberInvited', { email: str(m.email), role: str(m.role) ?? '' }) : null;
    case 'MEMBER_ROLE_CHANGED':
      return str(m.from) && str(m.to) ? t('audit.summary.fromTo', { from: str(m.from), to: str(m.to) }) : null;
    case 'MEMBER_REMOVED':
      return t('audit.summary.memberRemoved', { name: str(m.name) ?? str(m.email) ?? '', slots: num(m.slotsArchived) ?? 0, sessions: num(m.sessionsRemoved) ?? 0 });
    case 'MEMBER_PLACEHOLDER_CREATED':
      return str(m.name);
    case 'MEMBER_CONVERTED':
      return [str(m.name), str(m.email)].filter(Boolean).join(' · ') || null;
    case 'BANK_PRIMARY_CHANGED':
      if (str(m.fromBankCode) && str(m.bankCode)) {
        return t('audit.summary.bankSwitch', { fromBank: str(m.fromBankCode), fromLast4: str(m.fromAccountLast4) ?? '', bank: str(m.bankCode), last4: str(m.accountLast4) ?? '' });
      }
      return str(m.bankCode) ? t('audit.summary.bank', { bank: str(m.bankCode), last4: str(m.accountLast4) ?? '' }) : null;
    case 'BANK_LINKED':
    case 'BANK_UNLINKED':
      return str(m.bankCode) ? t('audit.summary.bank', { bank: str(m.bankCode), last4: str(m.accountLast4) ?? '' }) : null;
    case 'BRANCH_CREATED':
    case 'BRANCH_ARCHIVED':
    case 'BRANCH_UNARCHIVED':
      return str(m.name);
    case 'BRANCH_SLOTS_ASSIGNED':
      return t('audit.summary.branchSlots', { count: num(m.slotsAssigned) ?? 0, name: str(m.name) ?? '' });
    case 'SUBSCRIPTION_UPGRADE_REQUESTED':
      return t('audit.summary.upgrade', { plan: str(m.planCode) ?? '', cycle: str(m.cycle) === 'ANNUAL' ? t('audit.summary.cycleAnnual') : t('audit.summary.cycleMonthly'), amount: formatVnd(num(m.amountVnd) ?? 0) });
    case 'SUBSCRIPTION_INVOICE_CANCELLED':
      return num(m.amountVnd) !== null ? formatVnd(num(m.amountVnd)) : null;
    case 'SUBSCRIPTION_RENEWAL_STOPPED':
    case 'SUBSCRIPTION_RENEWAL_RESUMED':
      return null;
    case 'INVOICE_UPDATED': {
      const code = str(m.invoiceCode) ?? '';
      const changed = m.changed && typeof m.changed === 'object' ? Object.keys(m.changed as object) : [];
      const fields = changed.map((f) => (i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${f}`) ? t(`audit.fields.${f}`) : f)).join(', ');
      const items = num(m.itemsAdded) ?? 0;
      if (fields) return t('audit.summary.invoiceUpdated', { code, fields });
      if (items > 0) return t('audit.summary.invoiceUpdatedItems', { code, count: items });
      return code || null;
    }
    case 'PAYMENT_RECORDED':
    case 'PAYMENT_LINKED': {
      const source = str(m.source);
      const src = source === 'CASH' ? t('audit.summary.sourceCash') : source === 'MANUAL_BANK' ? t('audit.summary.sourceBank') : source === 'SEPAY' ? t('audit.summary.sourceSepay') : null;
      const base = t('audit.summary.paymentFor', { amount: formatVnd(num(m.amountVnd) ?? 0), code: str(m.invoiceCode) ?? '' });
      return src ? `${base} · ${src}` : base;
    }
    case 'PAYMENT_IGNORED':
      return [formatVnd(num(m.amountVnd) ?? 0), str(m.content)].filter(Boolean).join(' · ');
    case 'PAYMENT_DELETED':
      return t('audit.summary.paymentFor', { amount: formatVnd(num(m.amountVnd) ?? 0), code: str(m.invoiceCode) ?? t('audit.summary.unlinked') });
    case 'INVOICE_DUE_DATE_BULK':
      return t('audit.summary.dueDateBulk', { count: num(m.count) ?? 0, date: str(m.dueDate)?.slice(0, 10) ?? '' });
    case 'EXPENSE_CREATED':
    case 'EXPENSE_DELETED':
      return [formatVnd(num(m.amountVnd) ?? 0), str(m.category), str(m.notes)].filter(Boolean).join(' · ');
    case 'EXPENSE_UPDATED': {
      const changed = m.changed && typeof m.changed === 'object' ? Object.keys(m.changed as object) : [];
      return changed.length ? changed.map((f) => (i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${f}`) ? t(`audit.fields.${f}`) : f)).join(', ') : null;
    }
    case 'PAYROLL_ADJUSTMENT_CREATED':
    case 'PAYROLL_ADJUSTMENT_DELETED':
      return t('audit.summary.payrollAdjust', { amount: formatVnd(num(m.amountVnd) ?? 0), month: str(m.monthLabel) ?? '' });
    case 'STUDENT_CREATED':
      return [str(m.name), str(m.clientName)].filter(Boolean).join(' · ') || null;
    case 'STUDENT_UPDATED': {
      const changed = m.changed && typeof m.changed === 'object' ? Object.keys(m.changed as object) : [];
      return changed.length ? changed.map((f) => (i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${f}`) ? t(`audit.fields.${f}`) : f)).join(', ') : null;
    }
    case 'STUDENTS_IMPORTED':
      return t('audit.summary.imported', { created: num(m.created) ?? 0, skipped: num(m.skipped) ?? 0 });
    case 'CLIENT_CREATED':
      return [str(m.name), str(m.phone)].filter(Boolean).join(' · ') || null;
    case 'CLIENT_DELETED':
      return str(m.name) ? t('audit.summary.withCount', { name: str(m.name), count: num(m.studentCount) ?? 0, unit: t('audit.summary.unitStudents') }) : null;
    case 'CLIENT_NOTE_DELETED':
      return str(m.contentPreview);
    case 'SUBJECT_CREATED':
      return [str(m.name), num(m.defaultRate) !== null ? formatVnd(num(m.defaultRate)) : null].filter(Boolean).join(' · ') || null;
    case 'SUBJECT_UPDATED':
    case 'SCHEDULE_TEMPLATE_UPDATED': {
      const field = str(m.field);
      if (!field) return null;
      const label = i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${field}`) ? t(`audit.fields.${field}`) : field;
      const show = (v: unknown) => (v === null || v === undefined || v === '' ? t('audit.summary.empty') : typeof v === 'number' && /Rate$/.test(field) ? formatVnd(v) : String(v).slice(0, 60));
      return t('audit.summary.fieldFromTo', { field: label, from: show(m.from), to: show(m.to) });
    }
    case 'SUBJECT_DELETED':
      return str(m.name) ? t('audit.summary.withCount', { name: str(m.name), count: num(m.enrollmentCount) ?? 0, unit: t('audit.summary.unitEnrollments') }) : null;
    case 'SCHEDULE_TEMPLATE_CREATED':
      return str(m.name);
    case 'SCHEDULE_TEMPLATE_DELETED':
    case 'SCHEDULE_TEMPLATE_ENTRIES_REPLACED':
      return str(m.name) ? t('audit.summary.withCount', { name: str(m.name), count: num(m.entries) ?? 0, unit: t('audit.summary.unitEntries') }) : null;
    case 'SLOT_CREATED':
    case 'SLOT_UNARCHIVED': {
      const day = num(m.dayOfWeek);
      // JS/Prisma weekday 0..6 → the i18n keys day1..day7 (Sunday first).
      return str(m.subjectName) ? t('audit.summary.slot', { subject: str(m.subjectName), day: day !== null ? t(`audit.summary.day${day + 1}`) : '', time: str(m.startTime) ?? '' }) : null;
    }
    case 'SLOT_ARCHIVED': {
      const day = num(m.dayOfWeek);
      return str(m.subjectName) ? t('audit.summary.slotMembers', { subject: str(m.subjectName), day: day !== null ? t(`audit.summary.day${day + 1}`) : '', time: str(m.startTime) ?? '', count: num(m.memberCount) ?? 0 }) : null;
    }
    case 'SLOT_UPDATED':
    case 'SESSION_UPDATED': {
      const field = str(m.field);
      if (!field) return null;
      const label = i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${field}`) ? t(`audit.fields.${field}`) : field;
      const show = (v: unknown) =>
        v === null || v === undefined || v === '' ? t('audit.summary.empty')
        : Array.isArray(v) ? v.map(String).join(', ')
        : field === 'startAt' && typeof v === 'string' ? formatHouseStamp(v)
        : field === 'dayOfWeek' && typeof v === 'number' && v >= 0 && v <= 6 ? t(`audit.summary.day${v + 1}`)
        : String(v).slice(0, 60);
      return t('audit.summary.fieldFromTo', { field: label, from: show(m.from), to: show(m.to) });
    }
    case 'SLOT_MEMBER_ADDED':
    case 'SLOT_MEMBER_REMOVED':
    case 'SESSION_PARTICIPANT_ADDED':
    case 'SESSION_PARTICIPANT_REMOVED':
      return str(m.studentName);
    case 'CLASS_CREATED':
      return str(m.displayName) ? t('audit.summary.classCreated', { displayName: str(m.displayName), slotCount: num(m.slotCount) ?? 0 }) : null;
    case 'CLASS_UPDATED': {
      const field = str(m.field);
      if (!field) return null;
      const label = i18n.exists(`${AUDIT_LABEL_NS}:audit.fields.${field}`) ? t(`audit.fields.${field}`) : field;
      return t('audit.summary.classUpdated', { field: label });
    }
    case 'CLASS_ARCHIVED':
    case 'CLASS_UNARCHIVED': {
      const slotCount = num(m.slotCount) ?? 0;
      const base = row.action === 'CLASS_ARCHIVED'
        ? t('audit.summary.classArchived', { slotCount })
        : t('audit.summary.classUnarchived', { slotCount });
      return m.cascadedFromSlot === true ? `${base} · ${t('audit.summary.classCascaded')}` : base;
    }
    case 'CLASS_MEMBERS_ADDED': {
      const n = (num(m.created) ?? 0) + (num(m.linked) ?? 0);
      return str(m.displayName) ? t('audit.summary.classMembersAdded', { n, displayName: str(m.displayName) }) : null;
    }
    case 'SESSION_CREATED': {
      const when = houseStamp(m.startAt);
      return str(m.subjectName) ? t('audit.summary.session', { subject: str(m.subjectName), when }) : null;
    }
    case 'SESSION_CANCELLED': {
      const when = houseStamp(m.startAt);
      return str(m.subjectName) ? t('audit.summary.sessionParticipants', { subject: str(m.subjectName), when, count: num(m.participantCount) ?? 0 }) : null;
    }
    case 'SESSION_PARTICIPANT_MOVED': {
      const when = houseStamp(m.toStartAt);
      return str(m.studentName) ? t('audit.summary.movedTo', { name: str(m.studentName), when }) : null;
    }
    case 'MONTH_REOPENED':
      return t('audit.summary.monthReopened', { month: m.month, year: m.year, count: num(m.count) ?? 0 });
    case 'ENROLLMENT_CREATED': {
      const mode = str(m.pricingType) === 'MONTHLY_FLAT' ? t('audit.summary.modeFlat') : str(m.billingPeriod) === 'TERM' ? t('audit.summary.modeTerm') : t('audit.summary.modeSession');
      const rate = num(m.ratePerSession) ?? num(m.monthlyRate);
      return rate !== null ? `${mode} · ${formatVnd(rate)}` : mode;
    }
    case 'ENROLLMENT_RATE_CHANGED':
      return num(m.from) !== null && num(m.to) !== null ? t('audit.summary.fromTo', { from: formatVnd(num(m.from)), to: formatVnd(num(m.to)) }) : null;
    case 'ENROLLMENT_CHARGE_ABSENT_CHANGED':
      return m.to === true ? t('audit.summary.chargeAbsentOn') : t('audit.summary.chargeAbsentOff');
    case 'ENROLLMENT_CHARGE_ABSENT_BULK':
      return t('audit.summary.chargeAbsentBulk', { count: num(m.updated) ?? 0, state: m.to === true ? t('audit.summary.on') : t('audit.summary.off') });
    case 'STUDENT_TAGS_CHANGED': {
      const added = Array.isArray(m.added) ? m.added.filter((x) => typeof x === 'string') : [];
      const removed = Array.isArray(m.removed) ? m.removed.filter((x) => typeof x === 'string') : [];
      const parts = [added.length ? t('audit.summary.tagsAdded', { tags: added.join(', ') }) : null, removed.length ? t('audit.summary.tagsRemoved', { tags: removed.join(', ') }) : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'PAYER_REASSIGNED':
      return num(m.pendingInvoiceCount) ? t('audit.summary.payerPending', { count: num(m.pendingInvoiceCount) }) : null;
    case 'SCHEDULE_TEMPLATE_APPLIED':
      return t('audit.summary.template', { name: str(m.templateName) ?? '', joined: num(m.joined) ?? 0, left: num(m.left) ?? 0 });
    case 'PAYROLL_FINALIZED':
      return [str(m.monthLabel), num(m.total) !== null ? formatVnd(num(m.total)) : null].filter(Boolean).join(' · ') || null;
    case 'PAYROLL_REOPENED':
      return str(m.monthLabel);
    case 'STUDENT_PROGRESS_LINK_BULK_CREATED':
      return t('audit.summary.progressLinks', { created: num(m.created) ?? 0, existing: num(m.existing) ?? 0 });
    case 'PLAN_ASSIGNED':
      return str(m.toPlanCode) ? t('audit.summary.fromTo', { from: str(m.fromPlanCode) ?? '?', to: str(m.toPlanCode) }) : null;
    case 'PROMOTION_APPLIED':
      return [str(m.name), num(m.percentOff) !== null ? `-${num(m.percentOff)}%` : null].filter(Boolean).join(' · ') || null;
    case 'INVITATION_REVOKED':
      return str(m.email);
    case 'INVOICE_CLIENT_CHANGED':
    case 'SLOT_MEMBER_MOVED':
    case 'ENROLLMENT_DELETED':
    case 'STUDENT_PROGRESS_LINK_CREATED':
    case 'STUDENT_PROGRESS_LINK_ROTATED':
    case 'SUBJECT_NOTE_TEMPLATE_UPDATED':
    case 'SUBJECT_NOTE_TEMPLATE_REMOVED':
    case 'STUDENT_DEACTIVATED':
    case 'STUDENT_REACTIVATED':
    case 'ENROLLMENT_PAUSED':
    case 'ENROLLMENT_RESUMED':
    case 'ENROLLMENT_ENDED':
      // Metadata is ids or the status pair the label already names ("Ngừng
      // học viên · to: INACTIVE" said it twice, the second time in English).
      return null;
    case 'USER_EMAIL_CHANGED':
      return str(m.to) ? t('audit.summary.fromTo', { from: str(m.from) ?? '?', to: str(m.to) }) : null;
    default: {
      const pairs = Object.entries(m)
        .filter(([k, v]) => !/Id$|Ids$|^at$/.test(k) && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
        // A cuid is an id whatever the key is called.
        .filter(([, v]) => !(typeof v === 'string' && /^c[a-z0-9]{20,}$/.test(v)))
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${String(v)}`);
      return pairs.length ? pairs.join(' · ') : null;
    }
  }
}
